/** Where the LaunchContext comes from at boot (`BootConfig.launchContextSourceMode`). */
export enum LaunchContextSourceMode {
  RemoteUrl = "remote_url",
  Fixture = "fixture",
}

/** Which embedded fixture LaunchContext to use when sourceMode = Fixture. */
export enum LaunchContextFixture {
  Desktop = "fixture_desktop",
  Mobile = "fixture_mobile",
  Unknown = "fixture_unknown",
}

/**
 * Resolves which LaunchContext source identifier the LaunchContextLoader
 * receives. Two paths:
 *
 * - Fixture mode → returns a `fixture_*` string. `?device=<value>` from
 *   the page URL takes precedence (`mobile`/`tablet`/`desktop`/`unknown`).
 * - RemoteUrl mode → returns the launch-context HTTP URL. `?device=<value>`
 *   is forwarded as a query param; the backend uses it as the source of
 *   truth for LaunchContext.clientType.
 *
 * PURE function — no mutation, no `this`.
 */
export function resolveLaunchContextSource(input: {
  sourceMode: LaunchContextSourceMode;
  launchContextFixture: LaunchContextFixture;
  launchContextBaseUrl: string;
  deviceOverride: string | null;
}): string {
  const { sourceMode, launchContextFixture, launchContextBaseUrl, deviceOverride } = input;

  if (sourceMode === LaunchContextSourceMode.Fixture) {
    // Fixture mode: ?device=<value> overrides `BootConfig.launchContextFixture`.
    // Whitelisted values (mobile|tablet|desktop|unknown) map 1:1 to
    // fixture_<value> names handled by LaunchContextLoader.tryResolveFixture.
    if (deviceOverride !== null) {
      return `fixture_${deviceOverride}`;
    }
    return launchContextFixture;
  }

  // Remote-URL mode: forward ?device=<value> as a query param. Backend
  // uses it as the LaunchContext source of truth. The composed URL ends in
  // its path (no query of its own) and may be relative (a same-origin
  // backend), which `new URL` would reject, hence the plain join.
  if (deviceOverride !== null) {
    return `${launchContextBaseUrl}?${new URLSearchParams({ device: deviceOverride }).toString()}`;
  }
  return launchContextBaseUrl;
}
